/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  DatabaseZap, CheckCircle2, XCircle, Loader2, ArrowRight, ArrowLeft,
  AlertTriangle, RotateCcw, Play, Square, Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useNotification } from '@/hooks/useNotification';
import { organizationsService } from '@/services/superadmin';
import migrationService, {
  type MigrationSourceSystem,
  type MigrationConnectionType,
  type MigrationJobType,
  type MigrationEntityType,
  type MigrationJob,
  type MigrationProgress,
  type AnalysisResult,
  SOURCE_CONNECTION_TYPES,
  CONNECTION_FIELDS,
} from '@/services/superadmin/migration.service';

const SOURCE_SYSTEMS: MigrationSourceSystem[] = [
  'SAGE_100C', 'ODOO', 'QUICKBOOKS_ONLINE', 'QUICKBOOKS_DESKTOP', 'DOLIBARR', 'GENERIC_CSV',
];

const ALL_ENTITY_TYPES: MigrationEntityType[] = [
  'TAX_RATE', 'UOM', 'CATEGORY', 'LOCATION', 'PARTNER',
  'PRODUCT', 'STOCK_LEVEL', 'PURCHASE_ORDER', 'SALES_ORDER', 'INVOICE',
];

const SCOPE_PRESETS = {
  masterData: ['TAX_RATE', 'UOM', 'CATEGORY', 'LOCATION', 'PARTNER', 'PRODUCT'] as MigrationEntityType[],
  openingBalances: ['TAX_RATE', 'UOM', 'CATEGORY', 'LOCATION', 'PARTNER', 'PRODUCT', 'STOCK_LEVEL'] as MigrationEntityType[],
  fullHistory: ALL_ENTITY_TYPES,
};

const STEPS = ['selectOrg', 'connect', 'chooseScope', 'analyze', 'progress', 'results'] as const;

export default function MigrationWizardPage() {
  const { t } = useTranslation('platform');
  const { success, error: showError } = useNotification();
  const [step, setStep] = useState(0);

  // Step 1 state
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [sourceSystem, setSourceSystem] = useState<MigrationSourceSystem>('SAGE_100C');
  const [jobType, setJobType] = useState<MigrationJobType>('BULK');

  // Step 2 state
  const [connectionType, setConnectionType] = useState<MigrationConnectionType>('SQL_SERVER');
  const [connectionConfig, setConnectionConfig] = useState<Record<string, string>>({});
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTested, setConnectionTested] = useState(false);

  // Step 3 state
  const [selectedScope, setSelectedScope] = useState<MigrationEntityType[]>(SCOPE_PRESETS.masterData);

  // Step 4 state
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);

  // Job state
  const [job, setJob] = useState<MigrationJob | null>(null);
  const [progress, setProgress] = useState<MigrationProgress | null>(null);
  const [polling, setPolling] = useState(false);

  // Fetch organizations
  const { data: orgsData } = useQuery({
    queryKey: ['organizations', 'migration-select'],
    queryFn: () => organizationsService.getOrganizations({ page: 0, size: 100 }),
  });

  // Update connection type when source system changes
  useEffect(() => {
    const types = SOURCE_CONNECTION_TYPES[sourceSystem];
    if (types && types.length > 0) {
      setConnectionType(types[0]);
    }
    setConnectionTested(false);
    setConnectionConfig({});
  }, [sourceSystem]);

  // Poll progress
  useEffect(() => {
    if (!polling || !job) return;
    const interval = setInterval(async () => {
      try {
        const p = await migrationService.getProgress(job.id);
        setProgress(p);
        if (['COMPLETED', 'COMPLETED_WITH_ERRORS', 'FAILED', 'CANCELLED'].includes(p.status)) {
          setPolling(false);
          const updatedJob = await migrationService.getJob(job.id);
          setJob(updatedJob);
          setStep(5); // results
        }
      } catch {
        // ignore polling errors
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [polling, job]);

  const handleTestConnection = useCallback(async () => {
    setTestingConnection(true);
    try {
      await migrationService.testConnection({ sourceSystem, connectionType, connectionConfig });
      setConnectionTested(true);
      success(t('migration.connection.connectionSuccess'));
    } catch (err: unknown) {
      setConnectionTested(false);
      const message = err instanceof Error ? err.message : t('migration.connection.connectionFailed');
      showError(message);
    } finally {
      setTestingConnection(false);
    }
  }, [sourceSystem, connectionType, connectionConfig, success, showError, t]);

  const handleAnalyze = useCallback(async () => {
    setAnalyzing(true);
    try {
      // Create job first
      const newJob = await migrationService.createJob({
        orgId: selectedOrgId,
        sourceSystem,
        jobType,
        connectionType,
        scope: selectedScope,
        connectionConfig,
      });
      setJob(newJob);

      // Analyze
      const result = await migrationService.analyzeJob(newJob.id);
      setAnalysisResult(result);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Analysis failed';
      showError(message);
    } finally {
      setAnalyzing(false);
    }
  }, [selectedOrgId, sourceSystem, jobType, connectionType, selectedScope, connectionConfig, showError]);

  const handleExecute = useCallback(async () => {
    if (!job) return;
    try {
      await migrationService.executeJob(job.id);
      setPolling(true);
      setStep(4); // progress
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Execution failed';
      showError(message);
    }
  }, [job, showError]);

  const handleCancel = useCallback(async () => {
    if (!job) return;
    try {
      const cancelled = await migrationService.cancelJob(job.id);
      setJob(cancelled);
      setPolling(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Cancel failed';
      showError(message);
    }
  }, [job, showError]);

  const handleRollback = useCallback(async () => {
    if (!job || !confirm(t('migration.results.rollbackConfirm'))) return;
    try {
      await migrationService.rollbackJob(job.id);
      success('Rollback complete');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Rollback failed';
      showError(message);
    }
  }, [job, t, success, showError]);

  const handleReset = () => {
    setStep(0);
    setJob(null);
    setProgress(null);
    setAnalysisResult(null);
    setConnectionTested(false);
  };

  const canProceed = () => {
    switch (step) {
      case 0: return !!selectedOrgId;
      case 1: return connectionType === 'CSV' || connectionTested;
      case 2: return selectedScope.length > 0;
      case 3: return !!analysisResult;
      default: return false;
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const renderStepIndicator = () => (
    <div className="mw-steps">
      {STEPS.map((s, i) => (
        <div key={s} className={`mw-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
          <div className="mw-step-dot">
            {i < step ? <CheckCircle2 size={16} /> : <span>{i + 1}</span>}
          </div>
          <span className="mw-step-label">{t(`migration.steps.${s}`)}</span>
        </div>
      ))}
    </div>
  );

  const renderStep0 = () => (
    <div className="mw-form">
      <div className="mw-field">
        <label>{t('migration.steps.selectOrg')}</label>
        <select value={selectedOrgId} onChange={e => setSelectedOrgId(e.target.value)}>
          <option value="">-- Select --</option>
          {orgsData?.content?.map((org: { id: string; name: string }) => (
            <option key={org.id} value={org.id}>{org.name}</option>
          ))}
        </select>
      </div>
      <div className="mw-field">
        <label>{t('migration.sourceSystem.label')}</label>
        <select value={sourceSystem} onChange={e => setSourceSystem(e.target.value as MigrationSourceSystem)}>
          {SOURCE_SYSTEMS.map(s => (
            <option key={s} value={s}>{t(`migration.sourceSystem.${s}`)}</option>
          ))}
        </select>
      </div>
      <div className="mw-field">
        <label>{t('migration.jobType.label')}</label>
        <div className="mw-radio-group">
          {(['BULK', 'INCREMENTAL'] as MigrationJobType[]).map(jt => (
            <label key={jt} className="mw-radio">
              <input type="radio" name="jobType" value={jt}
                     checked={jobType === jt} onChange={() => setJobType(jt)} />
              <span>{t(`migration.jobType.${jt}`)}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep1 = () => {
    const availableTypes = SOURCE_CONNECTION_TYPES[sourceSystem] || [];
    const fields = CONNECTION_FIELDS[connectionType] || [];

    return (
      <div className="mw-form">
        <div className="mw-field">
          <label>{t('migration.connectionType.label')}</label>
          <select value={connectionType}
                  onChange={e => { setConnectionType(e.target.value as MigrationConnectionType); setConnectionTested(false); }}>
            {availableTypes.map(ct => (
              <option key={ct} value={ct}>{t(`migration.connectionType.${ct}`)}</option>
            ))}
          </select>
        </div>

        {fields.map(field => (
          <div key={field} className="mw-field">
            <label>{t(`migration.connection.${field}`)}</label>
            <input
              type={field === 'password' ? 'password' : 'text'}
              value={connectionConfig[field] || ''}
              placeholder={field === 'port' ? (connectionType === 'SQL_SERVER' ? '1433' : '5432') : ''}
              onChange={e => setConnectionConfig(prev => ({ ...prev, [field]: e.target.value }))}
            />
          </div>
        ))}

        {fields.length > 0 && (
          <div className="mw-actions-row">
            <Button onClick={handleTestConnection} disabled={testingConnection}
                    variant={connectionTested ? 'primary' : 'outline'}>
              {testingConnection ? <Loader2 size={16} className="animate-spin" /> :
               connectionTested ? <CheckCircle2 size={16} /> : null}
              <span style={{ marginLeft: 6 }}>{t('migration.connection.testConnection')}</span>
            </Button>
            {connectionTested && (
              <span className="mw-success-text">
                <CheckCircle2 size={14} /> {t('migration.connection.connectionSuccess')}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderStep2 = () => (
    <div className="mw-form">
      <div className="mw-presets">
        {(Object.keys(SCOPE_PRESETS) as Array<keyof typeof SCOPE_PRESETS>).map(preset => (
          <button key={preset} className={`mw-preset ${JSON.stringify(selectedScope) === JSON.stringify(SCOPE_PRESETS[preset]) ? 'active' : ''}`}
                  onClick={() => setSelectedScope([...SCOPE_PRESETS[preset]])}>
            <strong>{t(`migration.scope.${preset}`)}</strong>
            <span>{t(`migration.scope.${preset}Desc`)}</span>
          </button>
        ))}
      </div>
      <div className="mw-checkbox-grid">
        {ALL_ENTITY_TYPES.map(et => (
          <label key={et} className="mw-checkbox">
            <input type="checkbox" checked={selectedScope.includes(et)}
                   onChange={e => {
                     if (e.target.checked) setSelectedScope(prev => [...prev, et]);
                     else setSelectedScope(prev => prev.filter(x => x !== et));
                   }} />
            <span>{t(`migration.entityType.${et}`)}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="mw-form">
      {!analysisResult && !analyzing && (
        <div className="mw-center">
          <Button onClick={handleAnalyze} size="lg">
            <Eye size={18} />
            <span style={{ marginLeft: 6 }}>{t('migration.analysis.title')}</span>
          </Button>
        </div>
      )}
      {analyzing && (
        <div className="mw-center">
          <Loader2 size={32} className="animate-spin" />
          <p>{t('migration.analysis.title')}...</p>
        </div>
      )}
      {analysisResult && (
        <div className="mw-analysis">
          <div className="mw-stat-row">
            <div className="mw-stat">
              <span className="mw-stat-label">{t('migration.analysis.totalEntities')}</span>
              <span className="mw-stat-value">{analysisResult.totalEntities.toLocaleString()}</span>
            </div>
            <div className="mw-stat">
              <span className="mw-stat-label">{t('migration.analysis.estimatedDuration')}</span>
              <span className="mw-stat-value">
                {analysisResult.estimatedDurationSeconds > 60
                  ? t('migration.analysis.minutes', { count: Math.ceil(analysisResult.estimatedDurationSeconds / 60) })
                  : t('migration.analysis.seconds', { count: analysisResult.estimatedDurationSeconds })}
              </span>
            </div>
          </div>
          <div className="mw-entity-counts">
            {Object.entries(analysisResult.entityCounts).map(([type, count]) => (
              <div key={type} className="mw-entity-count">
                <span>{t(`migration.entityType.${type}`)}</span>
                <span className="mw-count">{(count as number).toLocaleString()}</span>
              </div>
            ))}
          </div>
          <div className="mw-center" style={{ marginTop: '1.5rem' }}>
            <Button onClick={handleExecute} size="lg">
              <Play size={18} />
              <span style={{ marginLeft: 6 }}>Start Migration</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => {
    const p = progress;
    const pct = p?.percentComplete || 0;

    return (
      <div className="mw-form">
        <div className="mw-progress-section">
          <div className="mw-progress-bar-container">
            <div className="mw-progress-bar" style={{ width: `${pct}%` }} />
          </div>
          <div className="mw-progress-pct">{pct}%</div>
        </div>
        {p && (
          <div className="mw-stat-row mw-progress-stats">
            <div className="mw-stat"><span className="mw-stat-label">{t('migration.progress.created')}</span><span className="mw-stat-value created">{p.createdCount}</span></div>
            <div className="mw-stat"><span className="mw-stat-label">{t('migration.progress.updated')}</span><span className="mw-stat-value updated">{p.updatedCount}</span></div>
            <div className="mw-stat"><span className="mw-stat-label">{t('migration.progress.skipped')}</span><span className="mw-stat-value skipped">{p.skippedCount}</span></div>
            <div className="mw-stat"><span className="mw-stat-label">{t('migration.progress.failed')}</span><span className="mw-stat-value failed">{p.failedCount}</span></div>
          </div>
        )}
        <div className="mw-center" style={{ marginTop: '1.5rem' }}>
          <Button variant="danger" onClick={handleCancel}>
            <Square size={16} />
            <span style={{ marginLeft: 6 }}>{t('migration.progress.cancel')}</span>
          </Button>
        </div>
      </div>
    );
  };

  const renderStep5 = () => {
    if (!job) return null;
    const isSuccess = job.status === 'COMPLETED';
    const isPartial = job.status === 'COMPLETED_WITH_ERRORS';

    return (
      <div className="mw-form">
        <div className={`mw-result-banner ${isSuccess ? 'success' : isPartial ? 'warning' : 'error'}`}>
          {isSuccess ? <CheckCircle2 size={24} /> : isPartial ? <AlertTriangle size={24} /> : <XCircle size={24} />}
          <span>{t(`migration.results.${isSuccess ? 'completed' : isPartial ? 'completedWithErrors' : job.status === 'CANCELLED' ? 'cancelled' : 'failed'}`)}</span>
        </div>
        <div className="mw-stat-row">
          <div className="mw-stat"><span className="mw-stat-label">{t('migration.progress.created')}</span><span className="mw-stat-value created">{job.createdCount}</span></div>
          <div className="mw-stat"><span className="mw-stat-label">{t('migration.progress.updated')}</span><span className="mw-stat-value updated">{job.updatedCount}</span></div>
          <div className="mw-stat"><span className="mw-stat-label">{t('migration.progress.skipped')}</span><span className="mw-stat-value skipped">{job.skippedCount}</span></div>
          <div className="mw-stat"><span className="mw-stat-label">{t('migration.progress.failed')}</span><span className="mw-stat-value failed">{job.failedCount}</span></div>
        </div>
        {job.errorMessage && (
          <div className="mw-error-msg">{job.errorMessage}</div>
        )}
        <div className="mw-actions-row" style={{ marginTop: '1.5rem' }}>
          <Button variant="outline" onClick={handleRollback}>
            <RotateCcw size={16} />
            <span style={{ marginLeft: 6 }}>{t('migration.results.rollback')}</span>
          </Button>
          <Button onClick={handleReset}>
            {t('migration.results.startNew')}
          </Button>
        </div>
      </div>
    );
  };

  const stepRenderers = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4, renderStep5];

  return (
    <div className="migration-wizard-page">
      <div className="mw-header">
        <div className="mw-header-icon"><DatabaseZap size={28} /></div>
        <div>
          <h1>{t('migration.title')}</h1>
          <p>{t('migration.subtitle')}</p>
        </div>
      </div>

      {renderStepIndicator()}

      <div className="mw-card">
        {stepRenderers[step]()}
      </div>

      {step < 3 && (
        <div className="mw-nav">
          <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={step === 0}>
            <ArrowLeft size={16} />
            <span style={{ marginLeft: 6 }}>Back</span>
          </Button>
          <Button onClick={() => { if (step === 2) { handleAnalyze(); setStep(3); } else setStep(s => s + 1); }}
                  disabled={!canProceed()}>
            <span style={{ marginRight: 6 }}>Next</span>
            <ArrowRight size={16} />
          </Button>
        </div>
      )}

      <style>{`
        /* ━━ ROOT ━━ */
        .migration-wizard-page {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          padding: 1.5rem 2rem;
          width: 100%;
          max-width: 900px;
          margin: 0 auto;
          box-sizing: border-box;
        }

        /* ━━ HEADER ━━ */
        .mw-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid var(--color-border-subtle);
          animation: mw-header-in 0.4s cubic-bezier(0.22,1,0.36,1) both;
        }
        .mw-header-icon {
          width: 48px; height: 48px;
          display: flex; align-items: center; justify-content: center;
          border-radius: var(--radius-lg);
          background: var(--color-brand-primary);
          color: var(--color-text-on-brand);
          flex-shrink: 0;
        }
        .mw-header h1 {
          font-family: var(--font-heading);
          font-size: 1.375rem;
          font-weight: 700;
          margin: 0;
          color: var(--color-text-primary);
        }
        .mw-header p {
          font-size: 0.8125rem;
          color: var(--color-text-secondary);
          margin: 0.125rem 0 0 0;
        }

        /* ━━ STEP INDICATOR ━━ */
        .mw-steps {
          display: flex;
          gap: 0.25rem;
          align-items: center;
          overflow-x: auto;
          animation: mw-card-in 0.4s cubic-bezier(0.22,1,0.36,1) 0.04s both;
        }
        .mw-step {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          border-radius: var(--radius-md);
          font-size: 0.8rem;
          color: var(--color-text-muted);
          white-space: nowrap;
          transition: color 0.2s, background 0.2s;
        }
        .mw-step.active {
          background: var(--color-bg-hover);
          color: var(--color-text-primary);
          font-weight: 600;
        }
        .mw-step.done { color: var(--color-brand-primary); }
        .mw-step-dot {
          width: 24px; height: 24px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 50%;
          border: 2px solid var(--color-border-default);
          font-size: 0.7rem; font-weight: 600;
          transition: all 0.2s ease;
        }
        .mw-step.active .mw-step-dot {
          border-color: var(--color-brand-primary);
          background: var(--color-brand-primary);
          color: var(--color-text-on-brand);
        }
        .mw-step.done .mw-step-dot {
          border-color: var(--color-brand-primary);
          color: var(--color-brand-primary);
        }
        .mw-step-label { display: none; }
        @media (min-width: 768px) { .mw-step-label { display: inline; } }

        /* ━━ CARD ━━ */
        .mw-card {
          background: var(--color-bg-secondary);
          border: 1px solid var(--color-border-subtle);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          box-shadow: var(--shadow-sm);
          animation: mw-card-in 0.4s cubic-bezier(0.22,1,0.36,1) 0.08s both;
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .mw-card:hover {
          box-shadow: var(--shadow-md);
        }

        /* ━━ FORM ━━ */
        .mw-form { display: flex; flex-direction: column; gap: 1.25rem; }

        .mw-field { display: flex; flex-direction: column; gap: 0.375rem; }
        .mw-field label {
          font-size: 0.8125rem;
          font-weight: 500;
          color: var(--color-text-primary);
        }
        .mw-field select, .mw-field input {
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--color-border-default);
          border-radius: var(--radius-md);
          background: var(--color-bg-primary);
          color: var(--color-text-primary);
          font-size: 0.875rem;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .mw-field select:hover, .mw-field input:hover {
          border-color: var(--color-border-strong);
        }
        .mw-field select:focus, .mw-field input:focus {
          outline: none;
          border-color: var(--color-brand-primary);
          box-shadow: var(--input-focus-ring);
        }

        /* ━━ RADIO GROUP ━━ */
        .mw-radio-group { display: flex; gap: 1rem; }
        .mw-radio {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          cursor: pointer;
          font-size: 0.875rem;
          color: var(--color-text-primary);
        }

        /* ━━ SCOPE PRESETS ━━ */
        .mw-presets {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 0.75rem;
        }
        .mw-preset {
          padding: 0.75rem 1rem;
          border: 2px solid var(--color-border-default);
          border-radius: var(--radius-lg);
          cursor: pointer;
          text-align: left;
          background: var(--color-bg-primary);
          transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
          display: flex; flex-direction: column; gap: 0.25rem;
        }
        .mw-preset:hover {
          border-color: var(--color-brand-primary);
          box-shadow: var(--shadow-sm);
        }
        .mw-preset.active {
          border-color: var(--color-brand-primary);
          background: var(--color-brand-secondary);
        }
        .mw-preset strong {
          font-size: 0.85rem;
          color: var(--color-text-primary);
        }
        .mw-preset span {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
        }

        /* ━━ CHECKBOX GRID ━━ */
        .mw-checkbox-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 0.5rem;
        }
        .mw-checkbox {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.85rem;
          cursor: pointer;
          color: var(--color-text-primary);
        }

        /* ━━ ACTIONS & STATUS ━━ */
        .mw-actions-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }
        .mw-success-text {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          color: var(--color-success);
          font-size: 0.85rem;
          font-weight: 500;
        }

        .mw-center {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          padding: 1.5rem 0;
          color: var(--color-text-secondary);
        }

        /* ━━ ANALYSIS RESULTS ━━ */
        .mw-analysis { display: flex; flex-direction: column; gap: 1rem; }

        .mw-stat-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 0.75rem;
        }
        .mw-stat {
          padding: 0.75rem;
          background: var(--color-bg-primary);
          border: 1px solid var(--color-border-subtle);
          border-radius: var(--radius-md);
          text-align: center;
          display: flex; flex-direction: column; gap: 0.25rem;
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .mw-stat:hover {
          box-shadow: var(--shadow-sm);
          transform: translateY(-1px);
        }
        .mw-stat-label {
          font-size: 0.6875rem;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--color-text-muted);
        }
        .mw-stat-value {
          font-family: var(--font-heading);
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--color-text-primary);
        }
        .mw-stat-value.created { color: var(--color-success); }
        .mw-stat-value.updated { color: var(--color-info); }
        .mw-stat-value.skipped { color: var(--color-text-muted); }
        .mw-stat-value.failed  { color: var(--color-error); }

        /* ━━ ENTITY COUNTS ━━ */
        .mw-entity-counts { display: flex; flex-direction: column; }
        .mw-entity-count {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 0.75rem;
          border-bottom: 1px solid var(--color-border-subtle);
          font-size: 0.85rem;
          color: var(--color-text-primary);
          transition: background 0.15s;
        }
        .mw-entity-count:hover {
          background: var(--color-bg-hover);
        }
        .mw-entity-count:last-child { border-bottom: none; }
        .mw-count {
          font-weight: 600;
          font-variant-numeric: tabular-nums;
          color: var(--color-brand-primary);
        }

        /* ━━ PROGRESS BAR ━━ */
        .mw-progress-section {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .mw-progress-bar-container {
          flex: 1;
          height: 12px;
          background: var(--color-bg-tertiary);
          border-radius: var(--radius-full);
          overflow: hidden;
        }
        .mw-progress-bar {
          height: 100%;
          background: var(--color-brand-primary);
          border-radius: var(--radius-full);
          transition: width 0.5s cubic-bezier(0.22,1,0.36,1);
        }
        .mw-progress-pct {
          font-family: var(--font-heading);
          font-size: 1.1rem;
          font-weight: 700;
          min-width: 50px;
          text-align: right;
          color: var(--color-text-primary);
        }

        /* ━━ RESULT BANNERS ━━ */
        .mw-result-banner {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 1.25rem;
          border-radius: var(--radius-lg);
          font-weight: 600;
          font-size: 1rem;
        }
        .mw-result-banner.success {
          background: var(--color-success-light);
          color: var(--color-success);
          border: 1px solid var(--color-border-success);
        }
        .mw-result-banner.warning {
          background: var(--color-warning-light);
          color: var(--color-warning);
          border: 1px solid var(--color-warning);
        }
        .mw-result-banner.error {
          background: var(--color-error-light);
          color: var(--color-error);
          border: 1px solid var(--color-border-error);
        }

        .mw-error-msg {
          padding: 0.75rem 1rem;
          border-radius: var(--radius-md);
          background: var(--color-error-light);
          border: 1px solid var(--color-border-error);
          color: var(--color-error);
          font-size: 0.85rem;
        }

        /* ━━ NAVIGATION ━━ */
        .mw-nav {
          display: flex;
          justify-content: space-between;
          animation: mw-card-in 0.4s cubic-bezier(0.22,1,0.36,1) 0.12s both;
        }

        /* ━━ ANIMATIONS ━━ */
        @keyframes mw-header-in {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes mw-card-in {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ━━ RESPONSIVE ━━ */
        @media (max-width: 640px) {
          .migration-wizard-page { padding: 1rem; }
          .mw-stat-row { grid-template-columns: repeat(2, 1fr); }
          .mw-presets { grid-template-columns: 1fr; }
        }

        /* ━━ ACCESSIBILITY ━━ */
        @media (prefers-reduced-motion: reduce) {
          .mw-header, .mw-steps, .mw-card, .mw-nav {
            animation: none !important;
          }
          .mw-stat, .mw-preset, .mw-card {
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
}
